import * as React from 'react';
import { cn } from '@frontdoor/ui/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';

const buttonVariants = cva(
  cn(
    'flex shrink-0 items-center justify-center gap-2',
    'rounded-full whitespace-nowrap',
    'font-sans text-sm font-medium',
    'transition-all outline-none',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2',
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
    'disabled:pointer-events-none disabled:opacity-50'
  ),
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary/90',
        destructive:
          'bg-destructive border-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline:
          'border-[1.5px] border-primary bg-white text-primary shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-hydrangea-500 text-white hover:bg-hydrangea-500/80',
        ghost: 'border-none hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline underline-offset-auto',
      },
      size: {
        default: 'h-13.25 px-6 sm:px-10 has-[>svg]:px-3',
        sm: 'h-9 gap-1.5 px-3 sm:px-4 has-[>svg]:px-2.5',
        lg: 'h-14 px-8 sm:px-12 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
